import { Alert, Button, Table, type TableColumnProps } from "zhihao-ui-react";
import { useCallback, useEffect, useMemo, useState, type Key, type ReactNode } from "react";
import { EmptyState } from "./Feedback";
import { SearchForm } from "./SearchForm";
import type { PageParams, PageResult, ProColumn, SearchField } from "./types";
import { TablePagination } from "./TablePagination";

export interface ProTableProps<T extends object> {
    columns: ProColumn<T>[];
    data?: T[];
    request?: (params: PageParams) => Promise<PageResult<T>>;
    requestAuto?: boolean;
    initialParams?: Record<string, unknown>;
    rowKey?: keyof T | ((row: T) => Key);
    pagination?: boolean;
    title?: ReactNode;
    header?: (selection: { rows: T[]; keys: Key[] }) => ReactNode;
    /** 将操作放进筛选栅格首位，对应旧版 ProTable 的 tableHeader 插槽。 */
    searchPrefix?: (selection: { rows: T[]; keys: Key[] }) => ReactNode;
    onRequestError?: (error: unknown) => void;
    onSelectionChange?: (rows: T[]) => void;
    /** 仅改变当前页可见行，不改变接口分页总数；用于旧版分组表格的本地展开/收起。 */
    displayData?: (rows: T[]) => T[];
}

/** 集成旧版搜索、远程请求、分页、选择及加载/空/错误状态的业务表格。 */
export function ProTable<T extends object>({
    columns,
    data,
    request,
    requestAuto = true,
    initialParams = {},
    rowKey = "id" as keyof T,
    pagination = true,
    title,
    header,
    searchPrefix,
    onRequestError,
    onSelectionChange,
    displayData
}: ProTableProps<T>) {
    const [rows, setRows] = useState<T[]>(data ?? []);
    const [total, setTotal] = useState(data?.length ?? 0);
    const [page, setPage] = useState(1);
    const [size, setSize] = useState(10);
    const [search, setSearch] = useState(initialParams);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string>();
    const [selectedKeys, setSelectedKeys] = useState<Key[]>([]);
    const [selectedRows, setSelectedRows] = useState<T[]>([]);
    const searchFields = useMemo<SearchField[]>(
        () =>
            columns
                .filter(column => column.search)
                .map((column, index) => ({
                    ...column.search!,
                    key: column.search?.key ?? String(column.key),
                    // Vue ProTable 会给未声明 order 的查询项依次赋值 index + 2。
                    order: column.search?.order ?? index + 2
                })),
        [columns]
    );

    /** 执行一次请求并原子更新正常、失败、空数据三类状态。 */
    const load = useCallback(
        async (nextPage = page, nextSize = size, nextSearch = search) => {
            if (!request) return;
            setLoading(true);
            setError(undefined);
            try {
                const result = await request({ ...initialParams, ...nextSearch, page: nextPage, size: nextSize });
                setRows(result.list);
                setTotal(result.total);
            } catch (reason) {
                setRows([]);
                setTotal(0);
                setError(reason instanceof Error ? reason.message : "请求失败");
                onRequestError?.(reason);
            } finally {
                setLoading(false);
            }
        },
        [initialParams, onRequestError, page, request, search, size]
    );

    useEffect(() => {
        if (data) {
            setRows(data);
            setTotal(data.length);
        }
    }, [data]);

    useEffect(() => {
        if (request && requestAuto) void load(1, size, search);
    }, []); // 仅首次自动加载，后续由交互触发。

    /** 分页控件沿用旧版“总数左置、页容量、页码、跳转”的固定顺序。 */
    const goToPage = (nextPage: number, nextSize = size) => {
        const maxPage = Math.max(1, Math.ceil(total / nextSize));
        const safePage = Math.min(Math.max(Number(nextPage) || 1, 1), maxPage);
        setPage(safePage);
        if (request) void load(safePage, nextSize, search);
    };
    const doSearch = (values: Record<string, unknown>) => {
        setSearch(values);
        setPage(1);
        if (request) void load(1, size, values);
    };

    /** 完整承接 Vue ColumnProps 中影响布局的字段，避免页面重新手写表格样式。 */
    const tableColumns: TableColumnProps<T>[] = columns.map(column => {
        const operationColumn = String(column.key).split(".").at(-1) === "operation";
        return {
            key: String(column.key),
            prop: String(column.key),
            label: column.title,
            width: column.width,
            minWidth: column.minWidth,
            align: column.align,
            fixed: column.fixed,
            className: column.className,
            showOverflowTooltip: column.ellipsis ?? column.showOverflowTooltip ?? !operationColumn,
            sorter: column.sortable,
            render: column.render
                ? scope => column.render!(scope.cellValue, scope.row, scope.rowIndex)
                : column.enum
                  ? scope => column.enum!.find(item => item.value === scope.cellValue)?.label ??
                        (scope.cellValue == null ? "--" : String(scope.cellValue))
                  : scope => scope.cellValue == null || scope.cellValue === "" ? "--" : String(scope.cellValue)
        };
    });

    // 旧 ProTable 只有显式声明选择行为时才展示勾选列，普通列表不能凭空多出复选框。
    /** zhihao-ui-react Table 通过 selection 类型列启用多选。 */
    const resolvedColumns: TableColumnProps<T>[] = onSelectionChange ? [{ type: "selection", width: 48 }, ...tableColumns] : tableColumns;
    const pagedRows = data && pagination ? rows.slice((page - 1) * size, page * size) : rows;
    const displayRows = displayData ? displayData(pagedRows) : pagedRows;
    const selection = { rows: selectedRows, keys: selectedKeys };
    const tableHeader = <>{header?.(selection)}{searchPrefix?.(selection)}</>;

    return (
        <section className="pro-table">
            {title ? <div className="pro-table__title"><strong>{title}</strong></div> : null}
            {header || searchPrefix || searchFields.length ? <div className="pro-table__toolbar">
                {header || searchPrefix ? <div className="pro-table__table-header">{tableHeader}</div> : null}
                {searchFields.length ? <SearchForm fields={searchFields} initialValues={initialParams} loading={loading} onSearch={doSearch} /> : null}
            </div> : null}
            {error ? (
                <Alert
                    type="error"
                    showIcon
                    title="数据加载失败"
                    description={error}
                    action={<Button onClick={() => void load()}>重试</Button>}
                />
            ) : null}
            <Table<T>
                rowKey={rowKey as never}
                columns={resolvedColumns}
                data={displayRows}
                empty={<EmptyState />}
                pagination={false}
                onSelectionChange={selected => {
                    const getKey = typeof rowKey === "function" ? rowKey : (row: T) => row[rowKey] as Key;
                    setSelectedKeys(selected.map(getKey));
                    setSelectedRows(selected);
                    onSelectionChange?.(selected);
                }}
            />
            {pagination ? <TablePagination current={page} pageSize={size} total={total} onChange={(nextPage, nextSize) => { setSize(nextSize); goToPage(nextPage, nextSize); }} /> : null}
        </section>
    );
}
